import { useId } from "react";
import { Package } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { DimensionCard } from "./DimensionCard";
import { MetricCard } from "./MetricCard";
import type { DailyTeamStats } from "../api/useTeamStats";

interface InventorySectionProps {
  daily: DailyTeamStats[];
  queueStats: {
    total: number;
    overdue: number;
    unassigned: number;
    byType: Record<string, number>;
  } | null;
}

export function InventorySection({ daily, queueStats }: InventorySectionProps) {
  const gradientId = useId();
  const total = queueStats?.total ?? 0;
  const overdue = queueStats?.overdue ?? 0;
  const unassigned = queueStats?.unassigned ?? 0;

  const chartData = daily.map((d) => ({
    date: new Date(d.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    open: d.totalItems,
  }));

  return (
    <DimensionCard title="Inventory" icon={Package} colorTheme="yellow" id="inventory">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <MetricCard
          label="Total Open"
          value={total}
          health={total < 100 ? "green" : total <= 200 ? "yellow" : "red"}
        />
        <MetricCard
          label="Overdue"
          value={overdue}
          health={overdue === 0 ? "green" : overdue <= 5 ? "yellow" : "red"}
        />
        <MetricCard label="Unassigned" value={unassigned} />
      </div>
      {chartData.length > 0 ? (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ca8a04" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#ca8a04" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: "#1f2937", border: "none", borderRadius: "6px", color: "#f9fafb", fontSize: "12px" }} />
              <Area type="monotone" dataKey="open" stroke="#ca8a04" strokeWidth={2} fill={`url(#${gradientId})`} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex items-center justify-center h-48 bg-gray-50 dark:bg-white/5 rounded-lg">
          <p className="text-sm text-gray-400">No trend data available</p>
        </div>
      )}
    </DimensionCard>
  );
}
